import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Product, Product_ProductUnitUsage } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { SaleProduct } from "gen/txn/models_pb"
import { useCallback, useMemo, useState } from "react"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"
import {
  convertUoMQuantityToQuantity,
  defaultUnitOfMeasureCode,
  defaultUnitOfMeasureLabel,
  getConversionFactorForUom,
  isValidUomSnapshot as isValidUomSnapshotShared,
  safeConvertQuantityToUoMQuantity,
  validateUomSelection,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"
import { UnitOfMeasure } from "shared-ts/libs/product/unitsOfMeasure.types"
import { logger } from "shared-ts/logger"

import { useAppDispatch } from "app/hooks"
import { useGetCompanyUnitsOfMeasure } from "features/admin/product/UnitsOfMeasure/getUnitsOfMeasure"

import { setSaleProductUom } from "../salesSlice"
import { isReturn } from "./saleProduct"

export type SaleUomUsage = "sale" | "return"

const uomLogger = logger.withScope("pos/sales/uom")

export function getSaleDefaultUomId(product?: Product): PBUUID | undefined {
  return product?.productDefaultUom?.[Product_ProductUnitUsage.SALE]?.uomId
}

export function isValidUomSnapshot(
  snapshot?: LedgerEntry_UomSnapshot
): snapshot is LedgerEntry_UomSnapshot {
  return isValidUomSnapshotShared(snapshot)
}

/**
 * Strict snapshot policy for Sales: missing/invalid snapshot => base-only.
 */
export function getSaleUomInfo(args: {
  saleProduct?: SaleProduct
  usage: SaleUomUsage
}): {
  uomId?: PBUUID
  conversionFactorToBase: Decimal
  displayCode: string
  displayLabel: string
} {
  const { saleProduct, usage } = args

  const uomId =
    usage === "return" ? saleProduct?.uomIdReturned : saleProduct?.uomId
  const snap =
    usage === "return"
      ? saleProduct?.uomSnapshotReturned
      : saleProduct?.uomSnapshot

  if (!uomId || !isValidUomSnapshot(snap) || !snap.conversionFactorToBase) {
    return {
      uomId: undefined,
      conversionFactorToBase: Decimal.fromBigSource(1),
      displayCode: defaultUnitOfMeasureCode,
      displayLabel: defaultUnitOfMeasureLabel,
    }
  }

  const factor = Decimal.fromPB(snap.conversionFactorToBase)
  return {
    uomId,
    conversionFactorToBase: factor,
    displayCode: snap.uomCode,
    displayLabel: `${snap.uomName} (${snap.uomCode})`,
  }
}

export function buildValidatedSaleUomSelection(args: {
  product?: Product
  uomId?: PBUUID
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
}): { uomId?: UUID; uomSnapshot?: LedgerEntry_UomSnapshot } {
  const { product, uomId, companyUnitsOfMeasureById } = args

  const result = validateUomSelection({
    product,
    uomId,
    companyUnitsOfMeasureById,
  })
  if (!result.ok) {
    if (result.reason !== "missing_uom_id") {
      uomLogger.error("Invalid UOM selection; falling back to base.", {
        reason: result.reason,
        productIdString: UUID.idString(product?.id),
        uomIdString: UUID.idString(uomId),
      })
    }
    return { uomId: undefined, uomSnapshot: undefined }
  }

  return { uomId: UUID.fromPB(result.uomId), uomSnapshot: result.uomSnapshot }
}

export function buildReturnUomSnapshot(args: {
  uomIdReturned?: PBUUID
  uomCode?: string
  conversionFactorToBase?: Decimal
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
}): LedgerEntry_UomSnapshot | undefined {
  const {
    uomIdReturned,
    uomCode,
    conversionFactorToBase,
    companyUnitsOfMeasureById,
  } = args

  if (
    !uomIdReturned ||
    !conversionFactorToBase ||
    !conversionFactorToBase.gt(Decimal.zero())
  ) {
    return undefined
  }

  const unit = companyUnitsOfMeasureById.get(UUID.idString(uomIdReturned))
  const code = uomCode ?? unit?.code
  const name = unit?.name
  if (!code || !name) {
    return undefined
  }

  return {
    uomCode: code,
    uomName: name,
    conversionFactorToBase,
  }
}

export function stripSaleProductUomFields(
  saleProduct: SaleProduct
): SaleProduct {
  const {
    uomIdReturned: _uomIdReturned,
    uomSnapshotReturned: _uomSnapshotReturned,
    quantityReturnedUom: _quantityReturnedUom,
    uomId: _uomId,
    uomSnapshot: _uomSnapshot,
    quantityUom: _quantityUom,
    ...rest
  } = saleProduct

  return SaleProduct.create(rest)
}

export function applySaleUomSelection(args: {
  saleProduct: SaleProduct
  usage: SaleUomUsage
  uomId?: PBUUID
}): SaleProduct {
  const { saleProduct, usage, uomId } = args

  const info = getSaleUomInfo({ saleProduct, usage })
  const shouldUseUom =
    !!uomId && !!info.uomId && UUID.eqFromPB(uomId, info.uomId)

  const baseQty =
    usage === "return"
      ? Decimal.fromPB(saleProduct.quantityReturnedV2 ?? Decimal.zero())
      : Decimal.fromPB(saleProduct.quantityV2 ?? Decimal.zero())

  const normalized: SaleProduct = {
    ...saleProduct,
    ...(usage === "return"
      ? { quantityReturnedV2: baseQty }
      : { quantityV2: baseQty }),
  }

  const stripped = stripSaleProductUomFields(normalized)
  // Base unit selection is represented by clearing uom fields.
  if (!shouldUseUom) {
    return stripped
  }

  const uomQty = safeConvertQuantityToUoMQuantity(
    baseQty,
    info.conversionFactorToBase
  )
  if (usage === "return") {
    return SaleProduct.create({
      ...stripped,
      uomIdReturned: uomId,
      uomSnapshotReturned: saleProduct.uomSnapshotReturned,
      quantityReturnedUom: uomQty,
      quantityReturnedV2: baseQty,
      // need to be set as we use these to retain UOM when transitioning from sale to return
      uomSnapshot: saleProduct.uomSnapshot,
      uomId: saleProduct.uomId,
    })
  }

  return SaleProduct.create({
    ...stripped,
    uomId,
    uomSnapshot: saleProduct.uomSnapshot,
    quantityUom: uomQty,
    quantityV2: baseQty,
    // need to be set as we use these to retain UOM when transitioning from return to sale
    uomSnapshotReturned: saleProduct.uomSnapshotReturned,
    uomIdReturned: saleProduct.uomIdReturned,
  })
}

export function useSelectSaleProductUom(
  productsById: Record<string, Product> | undefined
): (saleProduct: SaleProduct, uomId?: PBUUID) => void {
  const dispatch = useAppDispatch()
  const { companyUnitsOfMeasureById } = useGetCompanyUnitsOfMeasure()

  return useCallback(
    (saleProduct: SaleProduct, uomId?: PBUUID) => {
      if (!saleProduct.id || !saleProduct.productId) return
      const usage = isReturn(saleProduct) ? "return" : "sale"
      const selection = buildValidatedSaleUomSelection({
        product: productsById?.[UUID.fromPB(saleProduct.productId).toString()],
        uomId,
        companyUnitsOfMeasureById,
      })
      dispatch(
        setSaleProductUom({
          id: saleProduct.id,
          usage,
          uomId: selection.uomId,
          uomSnapshot: selection.uomSnapshot,
        })
      )
    },
    [companyUnitsOfMeasureById, dispatch, productsById]
  )
}

interface UseSelectSaleProductPricingUomResult {
  selectSaleProductPricingUom: (spId?: UUID, uomId?: UUID) => void
  pricingUomIdBySaleProductId: Record<string, UUID | undefined>
}

export function useSelectSaleProductPricingUom(
  saleProducts: SaleProduct[],
  productsById?: Record<string, Product>
): UseSelectSaleProductPricingUomResult {
  // Pricing UOM selection per sale product:
  // - undefined: uninitialized -> product default PRICING UOM
  // - null: explicitly selected base unit
  const [
    pricingUomSelectionBySaleProductId,
    setPricingUomSelectionBySaleProductId,
  ] = useState<Record<string, UUID | null | undefined>>({})

  const selectSaleProductPricingUom = useCallback(
    (spId?: UUID, uomId?: UUID) => {
      if (!spId) return
      setPricingUomSelectionBySaleProductId((prev) => ({
        ...prev,
        [UUID.idString(spId)]: uomId ?? null,
      }))
    },
    []
  )

  const pricingUomIdBySaleProductId = useMemo(() => {
    const out: Record<string, UUID | undefined> = {}
    const products = productsById ?? {}

    for (const sp of saleProducts) {
      if (!sp.id) continue
      const spIdString = UUID.fromPB(sp.id).toString()

      const selection = pricingUomSelectionBySaleProductId[spIdString]
      if (selection === null) {
        // Base unit
        out[spIdString] = undefined
        continue
      }
      if (selection) {
        // Pricing UOM
        out[spIdString] = selection
        continue
      }

      // Default pricing UOM
      const fullProduct = sp.productId
        ? products[UUID.fromPB(sp.productId).toString()]
        : undefined
      const candidate = UUID.fromPBOrUndefined(
        fullProduct?.productDefaultUom?.[Product_ProductUnitUsage.PRICING]
          ?.uomId
      )
      const factor = getConversionFactorForUom(fullProduct, candidate)
      out[spIdString] =
        factor && factor.gt(Decimal.zero()) ? candidate : undefined
    }

    return out
  }, [pricingUomSelectionBySaleProductId, productsById, saleProducts])

  return { selectSaleProductPricingUom, pricingUomIdBySaleProductId }
}

type UnitOfMeasureMode = "base" | "linked" | "unlinked"

export function getUnitOfMeasureMode(
  isLinkedUomMode: boolean,
  enableUom: boolean,
  unlinkedUomIdPresent: boolean,
  unlinkedConversionFactor?: Decimal
): UnitOfMeasureMode {
  if (isLinkedUomMode) return "linked"
  const canUnlinked =
    enableUom &&
    unlinkedUomIdPresent &&
    !!unlinkedConversionFactor &&
    unlinkedConversionFactor.gt(Decimal.zero())
  return canUnlinked ? "unlinked" : "base"
}

export function toBaseQuantityFromInput(
  inputQuantity: Decimal,
  mode: UnitOfMeasureMode,
  linkedConversionFactor?: Decimal,
  unlinkedConversionFactor?: Decimal
): Decimal {
  if (mode === "base") {
    return inputQuantity
  }
  // Return quantity is always negative in this modal.
  const signedUom = inputQuantity.abs().mult(Decimal.fromBigSource(-1))
  const factor =
    mode === "linked" ? linkedConversionFactor : unlinkedConversionFactor
  return convertUoMQuantityToQuantity(signedUom, factor)
}

export function buildUnlinkedReturnUomFields(
  mode: UnitOfMeasureMode,
  quantityBase: Decimal,
  linkedUomId?: UUID,
  linkedConversionFactor?: Decimal,
  unlinkedUomId?: UUID,
  unlinkedConversionFactor?: Decimal
): { uomIdReturned?: UUID; quantityReturnedUom?: Decimal } {
  if (mode === "base") {
    return { uomIdReturned: undefined, quantityReturnedUom: undefined }
  }

  const uomIdReturned = mode === "linked" ? linkedUomId : unlinkedUomId
  const factor =
    mode === "linked" ? linkedConversionFactor : unlinkedConversionFactor
  const quantityReturnedUom = safeConvertQuantityToUoMQuantity(
    quantityBase,
    factor
  ).abs()

  return { uomIdReturned, quantityReturnedUom }
}
